import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Text,
  useBreakpointValue,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { encode } from 'base64-arraybuffer';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import { del, put } from '../../../api/api-helpers';
import { Tenant } from '../../../api/api-tenant';
import { useAppDispatch } from '../../../app/hooks';
import { baseAddressSchema } from '../../../common/components/address/select-address-helpers';
import { SelectAddress } from '../../../common/components/address/SelectAddress';
import { CustomButton } from '../../../common/components/CustomButton';
import { MobileTopNav } from '../../../common/components/MobileTopNav';
import { BOTTOM_NAV_HEIGHT } from '../../../common/constants';
import { addUserDetails, selectUser } from '../userSlice';
import { BusinessData, mapBusinessApiDataToForm, mapBusinessFormDataToAPI } from './business-details-helpers';
import { UploadBusinessLogo, UploadedFile } from './UploadBusinessLogo';

const schema = yup
  .object()
  .shape({
    name: yup.string().trim().required('*Name is Required'),
    address: yup.string().trim().required('*Address Name is Required'),
    size: yup.string().trim(),
  })
  .concat(baseAddressSchema);

export function BusinessDetails() {
  const methods = useForm<BusinessData>({
    resolver: yupResolver(schema),
  });
  const {
    register,
    handleSubmit: handleFormSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = methods;
  const isMobileViewport = useBreakpointValue({
    base: true,
    xl: false,
  });

  const background = useColorModeValue('white', 'gray.900');
  const [{ isFormFilled, isLoading }, setState] = useState({
    isFormFilled: false,
    isLoading: false,
  });
  const [{ logoFile, isUploading, isDeleting, isDeleteModalOpen }, setLogoFile] = useState({
    logoFile: null as UploadedFile | null,
    isUploading: false,
    isDeleteModalOpen: false,
    isDeleting: false,
  });
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const user = useSelector(selectUser);
  const toast = useToast({ isClosable: true });
  const dispatch = useAppDispatch();

  useEffect(() => {
    const tenantDetails = user.tenant;

    reset(mapBusinessApiDataToForm(tenantDetails));
    if (tenantDetails.logo_url) {
      setLogoFile((prevState) => ({
        ...prevState,
        logoFile: {
          url: tenantDetails.logo_url || '',
          fileData: null,
          extension: '',
          isPersisted: true,
        },
      }));
    }

    // Check if all fields are filled and enable save details button.
    // Using subscription so that we do not rerender whole component on value changes

    const subscription = watch((values) => {
      const isFormFilled = Object.entries(values).every(([key, value]) => {
        // size is optional
        if (key === 'size') return true;
        return !!value;
      });
      setState((prevState) => ({ ...prevState, isFormFilled }));
    });
    return () => subscription.unsubscribe();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch]);

  const handleCloseDeleteModal = () => {
    setLogoFile((prevState) => ({
      ...prevState,
      isDeleteModalOpen: false,
    }));
  };

  async function handleSubmit(values: BusinessData) {
    setState((prevState) => ({ ...prevState, isLoading: true }));

    try {
      const updatedTenant = await put<Tenant>(`/fleet/tenants/${user.tenant_id}`, mapBusinessFormDataToAPI(values));
      reset(mapBusinessApiDataToForm(updatedTenant));
      toast({
        title: 'Successfully updated business details.',
        status: 'success',
      });
      dispatch(addUserDetails({ tenant: updatedTenant }));
    } catch (error) {
      toast({
        title: 'Something went wrong updating business details.',
        description: 'Please try again. If this continues, please contact support.',
        status: 'error',
      });
    }

    setState((prevState) => ({ ...prevState, isLoading: false }));
  }

  return (
    <FormProvider {...methods}>
      <Box px={[0, 0, 10]}>
        {isMobileViewport ? (
          <MobileTopNav title={'Business Details'} backURL={'/account/home'} />
        ) : (
          <Button as={Link} to={'/account/home'} mt={3}>
            Back
          </Button>
        )}
        <Center mx={isMobileViewport ? 0 : 10}>
          <Box
            w={isMobileViewport ? '100%' : '50%'}
            minH={isMobileViewport ? '90vh' : '100%'}
            bg={background}
            borderRadius={isMobileViewport ? 0 : 10}
            overflow="hidden"
          >
            {isMobileViewport || (
              <Heading mb={5} size={'lg'} paddingLeft={isMobileViewport ? 0 : 6} paddingTop={isMobileViewport ? 0 : 6}>
                Business Details
              </Heading>
            )}
            <Divider />

            <Box onSubmit={handleFormSubmit(handleSubmit)} as={'form'} px={4} data-testid="business-details-form">
              <FormControl mt={4} isInvalid={!!errors.name}>
                <FormLabel>Business name</FormLabel>
                <Input {...register('name')} placeholder="Enter business name" data-testid="business-name" />
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>

              {/* Temporarily hidden until implemented in api */}
              {/* <FormControl isInvalid={!!errors.size} mt={3}>
                <FormLabel>Company Size (Optional)</FormLabel>
                <Select {...register('size')}>
                  <option hidden disabled value="">
                    Select option
                  </option>
                  <option value="5-10">5-10</option>
                  <option value="10-20">10-20</option>
                  <option value="20-50">20-50</option>
                  <option value="50-100">50-100</option>
                  <option value="100+">100+</option>
                </Select>
                <FormErrorMessage>{errors.size?.message}</FormErrorMessage>
              </FormControl> */}

              <SelectAddress<BusinessData>
                testId="business-address"
                inputTitle="Business address"
                containerProps={{ mt: 4 }}
                predictionContainerProps={{ width: '100%' }}
              />

              <FormControl my={4} data-testid="business-logo">
                <FormLabel>Upload logo (Optional)</FormLabel>
                <Text>You logo will be displayed in various parts of the app for you & your team members</Text>

                <UploadBusinessLogo
                  isUploading={isUploading || isDeleting}
                  onDeleteFile={async (file: UploadedFile) => {
                    if (file.isPersisted) {
                      setLogoFile((prevState) => ({ ...prevState, isDeleteModalOpen: true }));
                    } else {
                      setLogoFile((prevState) => ({ ...prevState, logoFile: null }));
                    }
                  }}
                  logoFile={logoFile}
                  onUploadImage={async (fileData: ArrayBuffer, extension: string) => {
                    const base64ImageData = encode(fileData);
                    const imageSizeInKB = Math.ceil((base64ImageData.length * 6) / 8 / 1024);

                    // Limit image size to 2MB
                    if (imageSizeInKB > 2048) {
                      toast({
                        title: 'Image Too Large',
                        description: 'Please try again. Maximum upload size is 2MB.',
                        status: 'error',
                      });
                      return;
                    }

                    setLogoFile((prevState) => ({
                      ...prevState,
                      logoFile: {
                        url: `data:image/${extension};base64,${base64ImageData}`,
                        fileData,
                        extension,
                        isPersisted: false,
                      },
                      isUploading: true,
                    }));

                    try {
                      const updatedTenant = await put<Tenant>(`/fleet/tenants/${user.tenant_id}/logo`, {
                        file_base64: encode(fileData),
                      });
                      setLogoFile((prevState) => ({
                        ...prevState,
                        isUploading: false,
                        /*
                          Since the logo url will not change as image uuid is not changed on update.
                          We need to add timestamp to invalidate browser cache.
                          https://stackoverflow.com/a/13067981
                        */
                        logoFile: {
                          url: `${updatedTenant.logo_url || ''}?current_time=${new Date().getTime()}`,
                          fileData: null,
                          extension: '',
                          isPersisted: true,
                        },
                      }));
                      dispatch(addUserDetails({ tenant: updatedTenant }));
                      toast({
                        title: 'Successfully uploaded logo!',
                        status: 'success',
                      });
                    } catch (error) {
                      toast({
                        title: 'Something went wrong uploading image.',
                        description: 'Please try again. If this continues, please contact support.',
                        status: 'error',
                      });
                      setLogoFile((prevState) => ({
                        ...prevState,
                        isUploading: false,
                        logoFile: null,
                      }));
                    }
                  }}
                />
              </FormControl>

              <CustomButton
                isLoading={isLoading}
                loadingText={'Saving details...'}
                data-testid="save-details-button"
                w="100%"
                rounded={3}
                my={0}
                mt={8}
                mb={BOTTOM_NAV_HEIGHT}
                isDisabled={!isFormFilled || !isDirty}
              >
                Save
              </CustomButton>
            </Box>
          </Box>
        </Center>

        <AlertDialog
          isOpen={isDeleteModalOpen}
          leastDestructiveRef={cancelRef as React.RefObject<HTMLButtonElement>}
          onClose={handleCloseDeleteModal}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent mx={4} data-testid={`delete-dialog`}>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Delete Logo
              </AlertDialogHeader>

              <AlertDialogBody>Are you sure you want to delete current logo?</AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={handleCloseDeleteModal} data-testid={`delete-cancel-button`}>
                  Cancel
                </Button>
                <Button
                  data-testid={`delete-confirm-button`}
                  colorScheme="red"
                  isLoading={isDeleting}
                  onClick={async () => {
                    setLogoFile((prevState) => ({ ...prevState, isDeleting: true }));

                    try {
                      await del<Tenant>(`/fleet/tenants/${user.tenant_id}/logo`, {});
                      setLogoFile((prevState) => ({
                        ...prevState,
                        isDeleting: false,
                        logoFile: null,
                        isDeleteModalOpen: false,
                      }));
                      dispatch(addUserDetails({ tenant: { ...user.tenant, logo_url: null } }));
                      toast({
                        title: 'Successfully deleted logo!',
                        status: 'success',
                      });
                    } catch (error) {
                      toast({
                        title: 'Something went wrong deleting image.',
                        description: 'Please try again. If this continues, please contact support.',
                        status: 'error',
                      });
                      setLogoFile((prevState) => ({
                        ...prevState,
                        isDeleting: false,
                        isDeleteModalOpen: false,
                      }));
                    }
                  }}
                  ml={3}
                >
                  Delete
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </Box>
    </FormProvider>
  );
}
